import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import InputWithTitleSection from '@components/InputWithTitleSection'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'

import { PopoverInner } from '../ShortlistProfileButton/index.styles'

interface EditNotePopoverProps {
  anchor: CustomPopoverProps['anchor']
  setAnchor: (
    event: Nullable<React.MouseEvent<HTMLElement>>,
    data?: { profileId: number; note: string; profileType: string },
  ) => void
  profileNoteData: Nullable<{ note?: string; profileId?: number; profileType: string }>
  shortlistId?: number
  deleteNoteHandler: (cb?: () => void) => void
  setConfirmExitPopupCloseAction: (action: () => void) => void
  updateNote: (data: { id: number; data: { note: string } }) => void
}

const EditNotePopover = ({
  anchor,
  setAnchor,
  profileNoteData,
  shortlistId,
  deleteNoteHandler,
  setConfirmExitPopupCloseAction,
  updateNote,
}: EditNotePopoverProps) => {
  const { t } = useTranslation()
  const [localEditNoteValue, setLocalEditNoteValue] = useState<string>(profileNoteData?.note || '')
  const { trackActivity } = useTrackUserActivity()

  const handleClose = () => {
    const edited = profileNoteData?.note !== localEditNoteValue.trim()

    if (edited) {
      setConfirmExitPopupCloseAction(() => {
        trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.NOTE.CLOSE, { profileType: profileNoteData?.profileType, shortlistId })
        setAnchor(null)
      })
      return false
    }

    trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.NOTE.CLOSE, { profileType: profileNoteData?.profileType, shortlistId })

    setAnchor(null)

    return true
  }

  const handleSubmit = (note: string, cb: () => void) => {
    if (profileNoteData?.profileId && profileNoteData?.note !== note) {
      updateNote({ id: profileNoteData?.profileId, data: { note: note.trim() } })
    } else {
      setAnchor(null)
    }

    trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.NOTE.ADD, { profileType: profileNoteData?.profileType, shortlistId })

    // slight delay to let ui react before clearing the field
    setTimeout(cb, 400)
  }

  const handleDelete = (cb?: () => void) => {
    trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.NOTE.DELETE, { profileType: profileNoteData?.profileType, shortlistId })
    cb?.()
  }

  const handleClearInput = () => {
    trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.NOTE.CLEAR, { profileType: profileNoteData?.profileType, shortlistId })
  }

  return (
    anchor && (
      <CustomPopover id="note_shortlist_profile_popover" anchor={anchor} setAnchor={handleClose}>
        <PopoverInner width={290}>
          <InputWithTitleSection
            multiline
            allowEmpty
            maxLength={100}
            onChange={val => setLocalEditNoteValue(val.trim())}
            initialValue={profileNoteData?.note}
            title={t(profileNoteData?.note ? translationKeys.EDIT_NOTE : translationKeys.ADD_NOTE)}
            label={t(translationKeys.ADD_NOTE_HERE)}
            onCancel={handleClose}
            middleButtonText={profileNoteData?.note ? (t(translationKeys.DELETE_NOTE) as string) : ''}
            middleButtonOnClick={handleDelete}
            submitButtonText={t(translationKeys.SAVE) as string}
            onSubmit={handleSubmit}
            onClear={handleClearInput}
          />
        </PopoverInner>
      </CustomPopover>
    )
  )
}

export default memo(EditNotePopover)
